import React, {
    useCallback, useState, useContext, useEffect
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { TranslatorContext, FormattedMessage } from '@jutro/locale';
import { Link, Loader } from '@jutro/components';
import { error } from '@jutro/logger';
import { Redirect } from 'react-router-dom';
import { useAuthentication } from 'gw-digital-auth-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { EmailUtil } from 'gw-portals-util-js';
import { getCookie, setCookie } from 'react-use-cookie';
import {
    withApplicationPropertyContext
} from 'tdic-components-platform-react';
import metadata from './EnrollmentComponent.metadata.json5';
import styles from './EnrollmentComponent.module.scss';

import messages from './EnrollmentComponent.messages';

const ENROLLMENT_ERRORS = {
    enrollmentInvalid: 'enrollmentInvalid',
    enrollmentFailed: 'enrollmentFailed',
    fieldEmpty: 'fieldEmpty',
    accountExist: 'accountExist',
    inactiveContact: 'inactiveContact',
    alreadyHasADANumber: 'alreadyHasADANumber'
};

function EnrollmentComponent(props) {
    const translator = useContext(TranslatorContext);
    const [formData, updateFormData] = useState({});
    const [enrollmentStatus, setEnrollmentStatus] = useState();
    const [availablePolicies, setAvailablePolicies] = useState([]);
    const [hasADAAuthority, setHasADAAuthority] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const {
        authHeader, refreshToken, email, user_name: userName, tdicLogout
    } = useAuthentication();
    const { EnrollmentService } = useDependencies('EnrollmentService');

    const {
        enrollmentType,
        onSuccessEnrollment,
        informationText,
        informationAdditionalText,
        returnLinkText,
        enrollButtonText,
        updateEnrolledPolicy
    } = props;

    const verifyHasADAAuthority = useCallback(() => {
        setLoading(true);
        EnrollmentService.hasADAAuthority(authHeader).then((status) => {
            setHasADAAuthority(status);
            setLoading(false);
        });
    }, [authHeader, EnrollmentService]);

    useEffect(() => {
        verifyHasADAAuthority();
    }, [verifyHasADAAuthority]);

    const enrollPolicy = useCallback(
        (evt) => {
            setEnrollmentStatus(undefined);
            if (evt) {
                evt.preventDefault();
            }

            const { policyNumber, zipCode, adaNumber } = formData;

            if (_.isEmpty(policyNumber) || _.isEmpty(zipCode) || _.isEmpty(adaNumber)) {
                setEnrollmentStatus(ENROLLMENT_ERRORS.fieldEmpty);
                return Promise.resolve();
            }

            return EnrollmentService.addEnrollmentRecord(
                {
                    details: {
                        policyNumber: policyNumber,
                        zipCode_Ext: zipCode,
                        adaNumber_Ext: adaNumber,
                        email: EmailUtil.checkEmailValidity(email) ? email : userName
                    },
                    type: _.get(appConfig, 'self-enrollment.tdicADAType', '') || enrollmentType
                },
                authHeader
            )
                .then((resp) => {
                    refreshToken().then(() => {
                        updateEnrolledPolicy(policyNumber);
                        const dnnRedirectionUrl = getCookie('DNN_Registration_Redirect');
                        if (dnnRedirectionUrl && dnnRedirectionUrl !== '') {
                            setCookie('DNN_Registration_Redirect', '');
                            window.open(dnnRedirectionUrl, '_self');
                        } else if (onSuccessEnrollment) {
                            onSuccessEnrollment();
                        } else {
                            updateFormData({});
                            setAvailablePolicies(resp);
                            setEnrollmentStatus('enrollmentRequestSucceeded');
                        }
                    });
                })
                .catch((err) => {
                    error(`EnrollmentComponent: enrollPolicy - Unable to enroll policy ${JSON.stringify(err)}`);
                    setAvailablePolicies([]);
                    if (err && err.baseError && err.baseError.includes('616')) {
                        setEnrollmentStatus(ENROLLMENT_ERRORS.enrollmentInvalid);
                    } else if (err && err.baseError && err.baseError.includes('620')) {
                        setEnrollmentStatus(ENROLLMENT_ERRORS.accountExist);
                    } else if (err && err.baseError && err.baseError.includes('625')) {
                        setEnrollmentStatus(ENROLLMENT_ERRORS.inactiveContact);
                    } else if (err && err.baseError && err.baseError.includes('626')) {
                        setEnrollmentStatus(ENROLLMENT_ERRORS.alreadyHasADANumber);
                    } else {
                        setEnrollmentStatus(ENROLLMENT_ERRORS.enrollmentFailed);
                    }
                });
        },
        [EnrollmentService, authHeader, email, enrollmentType, formData,
            onSuccessEnrollment, refreshToken,
            updateEnrolledPolicy, userName]
    );

    const writeValue = useCallback(
        (value, path) => updateFormData({
            ...formData,
            [path]: value
        }),
        [formData]
    );

    const getSuccessNotificationMessage = useCallback(() => {
        return (
            <div>
                <span>
                    {availablePolicies.length > 1
                        ? translator(messages.accessToYourPolicies)
                        : translator(messages.accessToYourPolicy)}
                </span>
                <span>{availablePolicies.join(', ')}</span>
            </div>
        );
    }, [availablePolicies, translator]);

    const doLogout = useCallback((event) => {
        event.preventDefault();
        tdicLogout();
    }, [tdicLogout]);

    const getAccountExistMessage = useCallback(() => {
        return (
            <FormattedMessage
                {...messages[enrollmentStatus]}
                values={{
                    link: <Link className={styles.signInLink} onClick={(event) => doLogout(event)} href="/">{translator(messages.signIn)}</Link>
                }}
            />
        );
    }, [doLogout, enrollmentStatus, translator]);

    const getInvalidErrorMessage = useCallback(() => {
        let linkLoc;
        let linkText;
        switch (enrollmentStatus) {
            case ENROLLMENT_ERRORS.enrollmentInvalid:
                linkLoc = _.get(
                    appConfig,
                    `tdic.howToFindDetailsExternalLink.${_.toLower(process.env.NODE_ENV)}`,
                    _.get(appConfig, 'tdic.howToFindDetailsExternalLink.development')
                );
                linkText = translator(messages.enrollmentInvalidLinkText);
                break;
            case ENROLLMENT_ERRORS.enrollmentFailed:
            default:
                linkLoc = _.get(
                    appConfig,
                    `tdic.learnMoreExternalLink.${_.toLower(process.env.NODE_ENV)}`,
                    _.get(appConfig, 'tdic.learnMoreExternalLink.development')
                );
                linkText = translator(messages.learnMore);
        }
        const externalLink = (
            <Link className={styles.signInLink} target="_blank" href={linkLoc}>
                {linkText}
            </Link>
        );
        return (
            <FormattedMessage
                {...messages[enrollmentStatus]}
                values={{
                    link: externalLink
                }}
            />
        );
    }, [enrollmentStatus, translator]);

    const getNotificationMessage = useCallback(() => {
        if (enrollmentStatus === 'accountExist') {
            return getAccountExistMessage();
        }
        if ((['enrollmentFailed', 'inactiveContact', 'enrollmentInvalid']).includes(enrollmentStatus)) {
            return getInvalidErrorMessage();
        }
        return translator(messages[enrollmentStatus]);
    }, [enrollmentStatus, getAccountExistMessage, getInvalidErrorMessage, translator]);

    const overrideProps = {
        verificationInformationText: {
            content: translator(informationText)
        },
        verificationInformationAdditionalText: {
            content: translator(informationAdditionalText),
            visible: !_.isUndefined(informationAdditionalText)
        },
        enrollmentErrorMessageContainer: {
            visible:
                !_.isUndefined(enrollmentStatus)
                && enrollmentStatus !== 'enrollmentRequestSucceeded'
        },
        enrollmentErrorMessage: {
            message: getNotificationMessage()
        },
        enrollmentSuccessNotificationContainer: {
            visible: enrollmentStatus === 'enrollmentRequestSucceeded'
        },
        enrollmentSuccessNotification: {
            message: availablePolicies.length !== 0 ? getSuccessNotificationMessage() : ''
        },
        returnLink: {
            to: '#',
            content: translator(returnLinkText),
            onClick: doLogout
        },
        enrollButton: {
            content: translator(enrollButtonText),
            trigger: enrollPolicy
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
    };

    if (hasADAAuthority) {
        const nextState = { pathname: '/login-page' };
        return <Redirect to={nextState} />;
    }

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }
    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={formData}
            overrideProps={overrideProps}
            onValueChange={writeValue}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

EnrollmentComponent.propTypes = {
    enrollmentType: PropTypes.string.isRequired,
    onSuccessEnrollment: PropTypes.func,
    updateEnrolledPolicy: PropTypes.func,
    returnLinkText: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string
    }),
    enrollButtonText: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string
    }),
    informationText: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string
    }).isRequired,
    informationAdditionalText: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string
    })
};

EnrollmentComponent.defaultProps = {
    onSuccessEnrollment: undefined,
    returnLinkText: messages.returnToHomePage,
    enrollButtonText: messages.enroll,
    informationAdditionalText: undefined,
    updateEnrolledPolicy: undefined
};

export default withApplicationPropertyContext(EnrollmentComponent);
